import { useEffect, useState } from 'react';
// Deep imports, not the '@jubuddy/buddy-core' barrel: this renders in the Jubit
// embed, which is in the main entry chunk, and the barrel keeps every campaign
// map and the siege/fusion data there with it (rule 050).
import { getSprite } from '@jubuddy/buddy-core/sprites/index.ts';
import { MASCOT_COLORS } from '@jubuddy/buddy-core/tokens/index.ts';
import type { BuddyId, MascotId } from '@jubuddy/buddy-core/types/ids.ts';

const BLINK_INTERVAL_MS = 4000;
const BLINK_DURATION_MS = 160;

/**
 * Animated ASCII buddy renderer. Loops `idle`, plays `blink` as an overlay
 * every ~4s. Honors `prefers-reduced-motion` by disabling blinks.
 */
export function BuddySprite(props: { buddyId: BuddyId; className?: string }) {
  const sprite = getSprite(props.buddyId);
  const color =
    (MASCOT_COLORS[props.buddyId as MascotId] as string | undefined) ?? '#F59E0B';

  const [blinking, setBlinking] = useState(false);

  useEffect(() => {
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const id = window.setInterval(() => {
      setBlinking(true);
      window.setTimeout(() => setBlinking(false), BLINK_DURATION_MS);
    }, BLINK_INTERVAL_MS + Math.floor(Math.random() * 1500));
    return () => window.clearInterval(id);
  }, []);

  const frame = blinking ? sprite.blink : sprite.idle;

  return (
    <pre
      role="img"
      aria-label={`${props.buddyId} buddy sprite`}
      className={`buddy-sprite animate-jub-float text-base ${props.className ?? ''}`}
      style={{ color }}
    >
      {frame.join('\n')}
    </pre>
  );
}
